import { useCallback, useState } from 'react';
import { UppyFile } from '@uppy/core';

import StatementDropzoneContent from 'modules/common/components/VerifiedStatementUpload/components/StatementDropzoneContent';
import StatementProcessingContent from 'modules/common/components/VerifiedStatementUpload/components/StatementProcessingContent';
import DragAndDrop, { ACCEPTED_FILE_TYPES_LABEL } from 'modules/common/components/DragAndDrop';
import { Document } from 'modules/common/interfaces/documents';
import STATEMENT_UPLOAD_ANALYTICS_EVENTS from 'services/statementUploadTasks/constants/analytics';

/** The document type every statement upload files under. Exported so callers' queries agree with it. */
export const STATEMENT_DOCUMENT_TYPE = 'BUSINESS_BANK_STATEMENT';

const MAX_FILES = 15;
const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/** The constraints line under the dropzone heading. The numbers are the constants above, so they cannot drift. */
const UPLOAD_HINT = `${ACCEPTED_FILE_TYPES_LABEL} · Up to ${MAX_FILE_SIZE_MB} MB each · ${MAX_FILES} files max`;

/**
 * The accessible name for the in-flight overlay.
 *
 * One stable summary rather than the visible text, which narrates through three stages in the first
 * ten seconds — announcing each would talk over the member for the whole upload. Counts everything
 * in flight, files going up as well as documents being read; zero is the gap between the two, where
 * the count is genuinely unknown.
 */
const PROCESSING_LABEL = (count: number) => {
    if (count === 0) {
        return 'Processing statements...';
    }
    return count === 1 ? 'Processing 1 statement...' : `Processing ${count} statements...`;
};

/** What `DragAndDrop` appends to `flowKey` to build the event name it emits. */
const UPLOAD_EVENT_SUFFIX = ' Document Uploaded';

/**
 * Derived from the named event, not restated beside it: `DragAndDrop` sends
 * `${flowKey}${UPLOAD_EVENT_SUFFIX}` while dashboards query the constant, and two spellings of one
 * event name is how a rename leaves the dropzone emitting the old one.
 */
const UPLOAD_FLOW_KEY = STATEMENT_UPLOAD_ANALYTICS_EVENTS.DOCUMENT_UPLOADED.slice(0, -UPLOAD_EVENT_SUFFIX.length);

const UPLOAD_CATEGORY = 'statement_upload';

interface StatementUploadAreaProps {
    /**
     * Files uploaded but not yet given a verdict. They get no row of their own, so this is the only
     * place the member is told they exist.
     */
    readonly processingCount: number;
    /**
     * Files in the same batch the server has already answered for — the other half of the batch
     * size the narration quotes. Without it the only number available shrinks as the work
     * progresses, so a member would watch their drop get smaller rather than get on.
     */
    readonly processedCount: number;
    /**
     * True while the work that follows the last answer is still running, so the screen does not
     * blink back to the idle prompt between the last verdict and the refresh it triggers. The
     * caller owns it because nothing observable from here separates "the batch just finished" from
     * "this modal opened on statements read yesterday".
     */
    readonly isFinalizing?: boolean;
    /**
     * When the oldest file still in flight was uploaded, as epoch milliseconds. The processing
     * screen times its narration from it, so closing and reopening the modal does not replay it.
     * Undefined while the batch is still going up and has no server timestamp yet.
     */
    readonly processingSinceMs?: number;
    /**
     * Whether a batch is on its way up, for a caller that has to gate on it. Forwarded straight
     * from the uploader rather than derived from `uploadingCount` below: the two disagree for the
     * window after the last byte lands and before the caller's files catch up, which is the window
     * a caller is asking about.
     */
    readonly onUploadingChange?: (isUploading: boolean) => void;
    /** Fires once per settled batch, not once per file. */
    readonly onUploadSuccess: (uploaded?: Document[]) => Promise<void>;
}

/**
 * The statement dropzone, with every constraint it advertises.
 *
 * One component rather than a per-caller `DragAndDrop` block: both upload surfaces configured it
 * identically, and a copy each lets the file-size limit a member is promised drift from the one
 * enforced beside it.
 */
export default function StatementUploadArea({
    processingCount,
    processedCount,
    isFinalizing = false,
    processingSinceMs = undefined,
    onUploadingChange = undefined,
    onUploadSuccess,
}: Readonly<StatementUploadAreaProps>) {
    /**
     * How many files are on their way up right now.
     *
     * The one stage the server cannot report on: until a file lands there is no document to have a
     * status. Uppy hands its queue back on every add and remove and clears it when the batch
     * settles, so this drops to zero exactly when the narration should stop saying "uploading".
     */
    const [uploadingCount, setUploadingCount] = useState(0);
    const handleFilesChanged = useCallback((files: UppyFile<any, any>[]) => setUploadingCount(files.length), []);

    const inFlightCount = uploadingCount + processingCount;

    /**
     * How big the drop is, for the narration that quotes it.
     *
     * The larger of the two observers rather than their sum: for one window both describe the same
     * files — the uploader still holds the batch in its queue while the poll has picked up the
     * first documents to land — and adding them would overstate what the member uploaded.
     */
    const statementCount = Math.max(uploadingCount, processedCount + processingCount);

    return (
        <DragAndDrop
            documentType={STATEMENT_DOCUMENT_TYPE}
            flowKey={UPLOAD_FLOW_KEY}
            category={UPLOAD_CATEGORY}
            uploadSource={UPLOAD_CATEGORY}
            maxNumberOfFiles={MAX_FILES}
            maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
            idleContent={<StatementDropzoneContent hint={UPLOAD_HINT} />}
            isSideLoading={processingCount > 0 || isFinalizing}
            // Replaces the dropzone's default spinner outright: this content animates its own
            // progress, and a spinner above it is a second thing moving for no extra information.
            loadingContent={
                <StatementProcessingContent statementCount={statementCount} startedAtMs={processingSinceMs} />
            }
            loadingLabel={PROCESSING_LABEL(inFlightCount)}
            onFilesChanged={handleFilesChanged}
            onUploadingChange={onUploadingChange}
            onUploadSuccess={onUploadSuccess}
        />
    );
}
